import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { getTagBroadcasts } from "./reportedHotspotApi";
import UserInfoModal from "../../../components/Common/UserInfoModal";

const BroadcastsModal = ({ isOpen, toggle, tagId, tagName }) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Filter states
  const [status, setStatus] = useState("all");
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // User info modal state
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const limit = 50;

  const fetchBroadcasts = useCallback(async (page = 1, append = false) => {
    if (!tagId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    try {
      const filters = {
        limit,
        page,
        status: status !== "all" ? status : undefined,
        dateRange: dateRange && dateRange !== "custom" ? dateRange : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const result = await getTagBroadcasts(tagId, filters);
      
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      if (append) {
        setBroadcasts((prev) => [...prev, ...result.broadcasts]);
      } else {
        setBroadcasts(result.broadcasts);
      }

      setCurrentPage(result.pagination.currentPage);
      setTotalPages(result.pagination.totalPages);
      setTotalRecords(result.pagination.totalRecords);
      setHasMore(result.pagination.currentPage < result.pagination.totalPages);
    } catch (error) {
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error("Error fetching broadcasts:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [tagId, limit, status, dateRange, startDate, endDate]);

  useEffect(() => {
    isMountedRef.current = true;
    if (isOpen && tagId) {
      setCurrentPage(1);
      setBroadcasts([]);
      fetchBroadcasts(1, false);
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, tagId, fetchBroadcasts]);

  // Refetch when filters change
  useEffect(() => {
    if (isOpen && tagId) {
      setCurrentPage(1);
      fetchBroadcasts(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dateRange, startDate, endDate]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchBroadcasts(currentPage + 1, true);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return <Badge color="success" className="px-2 py-1">Active</Badge>;
      case "expired":
        return <Badge color="secondary" className="px-2 py-1">Expired</Badge>;
      default:
        return <Badge color="secondary" className="px-2 py-1">{status || "Unknown"}</Badge>;
    }
  };

  const getModerationBadge = (moderation) => {
    if (!moderation) return <Badge color="secondary">No Moderation</Badge>;
    if (moderation.moderation_pass) {
      return <Badge color="success">Passed</Badge>;
    }
    return <Badge color="danger">Failed</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleViewUserInfo = useCallback((userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleClose = () => {
    setBroadcasts([]);
    setCurrentPage(1);
    setStatus("all");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setUserInfoModal(false);
    setSelectedUserId(null);
    toggle();
  };

  const clearFilters = () => {
    setStatus("all");
    setDateRange("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="xl" className="broadcasts-modal">
      <ModalHeader className="bg-primary text-white" toggle={handleClose}>
        <div className="d-flex align-items-center">
          <i className="mdi mdi-broadcast me-2"></i>
          <span>Broadcasts for Tag #{tagId} - {tagName}</span>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }} className="p-3">
        {/* Filters */}
        <Card className="mb-3 shadow-sm">
          <CardBody className="p-3">
            <Row className="g-3">
              <Col md={3}>
                <FormGroup>
                  <Label className="small">Status</Label>
                  <Input
                    type="select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    size="sm"
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label className="small">Date Range</Label>
                  <Input
                    type="select"
                    value={dateRange}
                    onChange={(e) => {
                      setDateRange(e.target.value);
                      if (e.target.value) {
                        setStartDate("");
                        setEndDate("");
                      }
                    }}
                    size="sm"
                  >
                    <option value="">None</option>
                    <option value="last_24_hours">Last 24 Hours</option>
                    <option value="last_7_days">Last 7 Days</option>
                    <option value="last_30_days">Last 30 Days</option>
                    <option value="custom">Custom Range</option>
                  </Input>
                </FormGroup>
              </Col>
              {dateRange === "custom" && (
                <>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="small">Start Date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        size="sm"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="small">End Date</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="sm"
                      />
                    </FormGroup>
                  </Col>
                </>
              )}
              <Col md={dateRange === "custom" ? 12 : 6} className="d-flex align-items-end">
                <Button color="secondary" size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {loading && broadcasts.length === 0 ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3 text-muted">Loading broadcasts...</p>
          </div>
        ) : broadcasts.length === 0 ? (
          <div className="text-center py-5">
            <i className="mdi mdi-broadcast-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
            <p className="mt-3 text-muted">No broadcasts found for this tag.</p>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <Badge color="info" className="px-3 py-2">
                Total Broadcasts: {totalRecords}
              </Badge>
            </div>
            {broadcasts.map((broadcast, index) => (
              <Card key={broadcast.broadcast_id || index} className="mb-3 shadow-sm">
                <CardBody className="p-3">
                  <Row className="g-3">
                    {broadcast.broadcastImageUrl && (
                      <Col md={3}>
                        <img
                          src={broadcast.broadcastImageUrl}
                          alt={broadcast.title}
                          className="img-fluid rounded"
                          style={{ maxHeight: "150px", width: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </Col>
                    )}
                    <Col md={broadcast.broadcastImageUrl ? 9 : 12}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1 fw-bold">{broadcast.title || "No Title"}</h6>
                          <div className="d-flex gap-2 mb-2">
                            {getStatusBadge(broadcast.status)}
                            {getModerationBadge(broadcast.moderation)}
                            {broadcast.Interest_count !== undefined && (
                              <Badge color="info" className="px-2 py-1">
                                <i className="mdi mdi-heart me-1"></i>
                                {broadcast.Interest_count} Interests
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {broadcast.description && (
                        <div className="mb-2">
                          <p className="text-muted small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                            {broadcast.description}
                          </p>
                        </div>
                      )}

                      <Row className="g-2 mt-2">
                        <Col md={6}>
                          <div className="mb-2">
                            <label className="form-label text-muted small mb-1">
                              <i className="mdi mdi-calendar-plus me-1"></i>Created At
                            </label>
                            <div className="small">{formatDate(broadcast.created_at)}</div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-2">
                            <label className="form-label text-muted small mb-1">
                              <i className="mdi mdi-calendar-clock me-1"></i>Expires At
                            </label>
                            <div className="small">{formatDate(broadcast.expiration_time)}</div>
                          </div>
                        </Col>
                        {broadcast.broadcaster && (
                          <Col md={6}>
                            <div className="mb-2">
                              <label className="form-label text-muted small mb-1">
                                <i className="mdi mdi-account me-1"></i>Broadcaster
                              </label>
                              <div className="d-flex align-items-center gap-2">
                                {broadcast.broadcaster.primaryImgURL && (
                                  <img
                                    src={broadcast.broadcaster.primaryImgURL}
                                    alt={broadcast.broadcaster.username}
                                    className="rounded-circle"
                                    style={{ width: "30px", height: "30px", objectFit: "cover" }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                )}
                                <div className="flex-grow-1">
                                  <div className="small fw-semibold">{broadcast.broadcaster.username || "N/A"}</div>
                                  <div className="text-muted small font-monospace">{broadcast.broadcaster.userId}</div>
                                </div>
                                {broadcast.broadcaster.userId && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleViewUserInfo(broadcast.broadcaster.userId)}
                                    className="px-2"
                                    title="View Broadcaster Details"
                                  >
                                    <i className="mdi mdi-eye"></i>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </Col>
                        )}
                        {broadcast.moderation && (
                          <Col md={6}>
                            <div className="mb-2">
                              <label className="form-label text-muted small mb-1">
                                <i className="mdi mdi-shield-check me-1"></i>Moderation
                              </label>
                              <div className="small">
                                {broadcast.moderation.moderation_decision && (
                                  <div>Decision: <strong>{broadcast.moderation.moderation_decision}</strong></div>
                                )}
                                {broadcast.moderation.moderation_reasons?.length > 0 && (
                                  <div className="mt-1">
                                    Reasons: {broadcast.moderation.moderation_reasons.join(", ")}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>

                      {/* Tags/Categories */}
                      {(broadcast.skill?.length > 0 || broadcast.interest?.length > 0 || 
                        broadcast.sport?.length > 0 || broadcast.school?.length > 0 ||
                        broadcast.degree?.length > 0 || broadcast.job?.length > 0) && (
                        <div className="mt-2 pt-2 border-top">
                          <label className="form-label text-muted small mb-1">Categories:</label>
                          <div className="d-flex flex-wrap gap-1">
                            {broadcast.skill?.map((s, idx) => (
                              <Badge key={idx} color="secondary" className="px-2 py-1">
                                Skill: {s.name}
                              </Badge>
                            ))}
                            {broadcast.interest?.map((i, idx) => (
                              <Badge key={idx} color="info" className="px-2 py-1">
                                Interest: {i.name}
                              </Badge>
                            ))}
                            {broadcast.sport?.map((s, idx) => (
                              <Badge key={idx} color="warning" className="px-2 py-1">
                                Sport: {s.name || s}
                              </Badge>
                            ))}
                            {broadcast.school?.map((s, idx) => (
                              <Badge key={idx} color="primary" className="px-2 py-1">
                                School: {s.name || s}
                              </Badge>
                            ))}
                            {broadcast.degree?.map((d, idx) => (
                              <Badge key={idx} color="success" className="px-2 py-1">
                                Degree: {d.name || d}
                              </Badge>
                            ))}
                            {broadcast.job?.map((j, idx) => (
                              <Badge key={idx} color="danger" className="px-2 py-1">
                                Job: {j.name || j}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-2 pt-2 border-top">
                        <span className="text-muted small font-monospace">Broadcast ID: {broadcast.broadcast_id}</span>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))}

            {hasMore && (
              <div className="text-center mt-3">
                <Button
                  color="primary"
                  outline
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-arrow-down me-2"></i>
                      Load More ({totalRecords - broadcasts.length} remaining)
                    </>
                  )}
                </Button>
              </div>
            )}

            {!hasMore && broadcasts.length > 0 && (
              <div className="text-center mt-3">
                <Badge color="secondary" className="px-3 py-2">
                  All broadcasts loaded
                </Badge>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="bg-light">
        <Button color="secondary" onClick={handleClose} className="px-4">
          <i className="mdi mdi-close me-2"></i>Close
        </Button>
      </ModalFooter>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh broadcasts after user action
          if (tagId) {
            fetchBroadcasts(currentPage, false);
          }
        }}
      />
    </Modal>
  );
};

export default BroadcastsModal;

